/**
 * 관리자용 평가사 관리 페이지
 *
 * EvalAdminPage.js에서 "평가사 관리" 버튼으로 진입
 * 또는 Dashboard.js에 별도 라우트로 추가
 *
 * 기능:
 * - 평가사 목록 (등급별 정렬, 평점/건수 표시)
 * - 평가사 상세 프로필 (→ InspectorDetail.js)
 * - 평가사 등록/수정
 * - 등급 자동 계산 (마스터/시니어/일반)
 */
import React, { useState, useEffect } from 'react';
import { API_ORIGIN, authFetch } from '../utils/api';
import InspectorDetail from './InspectorDetail';
import CustomModal from './CustomModal';

// ─── 상수 ───
var LEVELS = {
  '마스터': { color: '#D85A30', bg: '#FAECE7', desc: '100건+ & 평점 4.5+', benefit: '검차비 +20%, 우선 콜' },
  '시니어': { color: '#534AB7', bg: '#EEEDFE', desc: '50건+ & 평점 4.0+', benefit: '검차비 +10%' },
  '일반':   { color: '#888780', bg: '#F1EFE8', desc: '기본', benefit: '기본 검차비' },
};

var LvBadge = function(props) {
  var level = props.level;
  var size = props.size || 'normal';
  var c = LEVELS[level] || LEVELS['일반'];
  var isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-block',
      padding: isSmall ? '1px 6px' : '3px 10px',
      borderRadius: 5,
      fontSize: isSmall ? 10 : 12,
      fontWeight: 600,
      color: c.color,
      background: c.bg,
    }}>{level}</span>
  );
};

var BASE = API_ORIGIN;

// 시간 경과 표시
var getTimeAgo = function(dateStr) {
  if (!dateStr) return '정보 없음';
  var diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return '방금 전';
  if (diff < 3600) return Math.floor(diff / 60) + '분 전';
  if (diff < 86400) return Math.floor(diff / 3600) + '시간 전';
  return Math.floor(diff / 86400) + '일 전';
};

// ─── 평가사 등록 폼 ───
function InspectorForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ name: '', phone: '', area: '', pin_code: '0000' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    border: '1px solid #e0dfda', borderRadius: 8, fontSize: 14, outline: 'none',
  };

  return (
    <div style={{
      background: '#fff', border: '2px solid #3B8BD4', borderRadius: 14,
      padding: 18, marginBottom: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#222' }}>평가사 등록</span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' }}>×</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>이름 *</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="홍길동" style={fieldStyle} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>전화번호 *</label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="010-0000-0000" style={fieldStyle} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>활동 지역</label>
          <input value={form.area} onChange={e => set('area', e.target.value)} placeholder="인천/경기서부" style={fieldStyle} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 3 }}>PIN 코드</label>
          <input value={form.pin_code} onChange={e => set('pin_code', e.target.value)} maxLength={4} placeholder="4자리" style={fieldStyle} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{
          padding: '9px 16px', borderRadius: 8, border: '1px solid #ddd',
          background: '#fff', cursor: 'pointer', fontSize: 13, color: '#666',
        }}>취소</button>
        <button onClick={() => { if (!form.name || !form.phone) { setModal({ type: 'danger', title: '실패', message: '이름과 전화번호는 필수', hideCancel: true, onConfirm: closeModal }); return; } onSubmit(form); }} style={{
          padding: '9px 20px', borderRadius: 8, border: 'none',
          background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}>등록</button>
      </div>
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}

// ─── 메인: 평가사 목록 ───
// API에서 상세 데이터 로딩
function InspectorDetailLoader({ id, fallback, onBack, onRefreshList }) {
  var [data, setData] = useState(null);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  var fetchDetail = function() {
    authFetch(BASE + '/api/eval/inspectors/' + id).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setData(d.data);
    }).catch(function() {});
  };
  useEffect(function() { fetchDetail(); }, [id]);
  var inspector = data || Object.assign({}, fallback, { reviews: [], avg_scores: { speed: 0, accuracy: 0, detail: 0 }, distribution: [] });
  var handleUpdate = function(inspId, form) {
    authFetch(BASE + '/api/eval/inspectors/' + inspId, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) { fetchDetail(); if (onRefreshList) onRefreshList(); }
      else setModal({ type: 'danger', title: '실패', message: d.error || '수정 실패', hideCancel: true, onConfirm: closeModal });
    }).catch(function() {});
  };
  return (<>
    <InspectorDetail inspector={inspector} onBack={onBack} onUpdate={handleUpdate} />
    {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
  </>);
}

export default function InspectorManage({ onBack }) {
  var [inspectors, setInspectors] = useState([]);
  var [selectedId, setSelectedId] = useState(null);
  var [showForm, setShowForm] = useState(false);
  var [sortBy, setSortBy] = useState('rating');
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };

  var fetchInspectors = function() {
    // include_inactive=1 — 셀프가입 승인대기(pending) + 비활성 평가사도 노출 (승인/관리 위해).
    authFetch(BASE + '/api/eval/inspectors?include_inactive=1').then(function(r) { return r.json(); }).then(function(d) {
      if (d.success) setInspectors(d.data || []);
    }).catch(function() {});
  };

  useEffect(function() { fetchInspectors(); }, []);

  // 셀프가입(pending) 승인 — PUT { status:'active' }. 승인 후 앱 로그인 가능.
  var approveInspector = function(ins) {
    setModal({
      type: 'confirm', title: '평가사 승인',
      message: ins.name + ' 평가사를 승인하시겠습니까?\n승인 후 앱에서 로그인할 수 있습니다.',
      confirmText: '승인',
      onConfirm: function() {
        closeModal();
        authFetch(BASE + '/api/eval/inspectors/' + ins.id, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'active' }),
        }).then(function(r) { return r.json(); }).then(function(d) {
          if (d.success) fetchInspectors();
          else setModal({ type: 'danger', title: '실패', message: d.error || '승인 실패', hideCancel: true, onConfirm: closeModal });
        }).catch(function() { setModal({ type: 'danger', title: '실패', message: '서버 연결 실패', hideCancel: true, onConfirm: closeModal }); });
      },
    });
  };

  // 정렬
  const sorted = [...inspectors].sort((a, b) => {
    if (sortBy === 'rating') return b.avg_rating - a.avg_rating;
    if (sortBy === 'jobs') return b.total_jobs - a.total_jobs;
    if (sortBy === 'recent') return b.recent_jobs - a.recent_jobs;
    return 0;
  });

  // 상세 페이지
  if (selectedId) {
    var inspector = inspectors.find(function(i) { return i.id === selectedId; });
    if (!inspector) { setSelectedId(null); return null; }
    return <InspectorDetailLoader id={selectedId} fallback={inspector} onBack={function() { setSelectedId(null); }} onRefreshList={fetchInspectors} />;
  }

  // 전체 통계
  const totalActive = inspectors.filter(i => i.status === 'active').length;
  // mysql DECIMAL → string '+' concat 위험 대응 (Number 강제)
  const avgRating = inspectors.length ? +(inspectors.reduce((s, i) => s + Number(i.avg_rating || 0), 0) / inspectors.length).toFixed(1) : 0;
  const totalJobs = inspectors.reduce((s, i) => s + i.total_jobs, 0);

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 12px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {onBack && (
            <button onClick={onBack} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 15, color: '#3B8BD4', padding: 0, fontWeight: 600,
            }}>←</button>
          )}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>평가사 관리</h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none',
          background: '#3B8BD4', color: '#fff', cursor: 'pointer',
          fontSize: 13, fontWeight: 700,
        }}>+ 평가사 등록</button>
      </div>

      {/* 등록 폼 */}
      {showForm && (
        <InspectorForm
          onSubmit={function(form) {
            authFetch(BASE + '/api/eval/inspectors', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(form),
            }).then(function(r) { return r.json(); }).then(function(d) {
              if (d.success) { fetchInspectors(); setShowForm(false); }
              else setModal({ type: 'danger', title: '실패', message: d.error || '등록 실패', hideCancel: true, onConfirm: closeModal });
            }).catch(function() { setModal({ type: 'danger', title: '실패', message: '서버 연결 실패', hideCancel: true, onConfirm: closeModal }); });
          }}
          onCancel={function() { setShowForm(false); }}
        />
      )}

      {/* 전체 통계 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#222' }}>{totalActive}</div>
          <div style={{ fontSize: 10, color: '#aaa' }}>활동 평가사</div>
        </div>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#EF9F27' }}>{avgRating}</div>
          <div style={{ fontSize: 10, color: '#aaa' }}>평균 평점</div>
        </div>
        <div style={{ background: '#f8f8f5', borderRadius: 10, padding: '10px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#222' }}>{totalJobs}</div>
          <div style={{ fontSize: 10, color: '#aaa' }}>총 검차</div>
        </div>
      </div>

      {/* 정렬 */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {[
          ['rating', '평점순'],
          ['jobs', '검차 수'],
          ['recent', '최근 활동'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setSortBy(key)} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: sortBy === key ? '1.5px solid #3B8BD4' : '1px solid #e0dfda',
            background: sortBy === key ? '#E6F1FB' : '#fff',
            color: sortBy === key ? '#185FA5' : '#888',
            fontWeight: sortBy === key ? 600 : 400,
          }}>{label}</button>
        ))}
      </div>

      {/* 평가사 카드 목록 */}
      {sorted.map(ins => {
        const ratingColor = ins.avg_rating >= 4.5 ? '#D85A30' : ins.avg_rating >= 3.5 ? '#534AB7' : '#888';
        const ratingBg = ins.avg_rating >= 4.5 ? '#FAECE7' : ins.avg_rating >= 3.5 ? '#EEEDFE' : '#F1EFE8';
        return (
          <div key={ins.id} onClick={() => setSelectedId(ins.id)} style={{
            background: '#fff', borderRadius: 14, border: '1px solid #eee',
            padding: 16, marginBottom: 8, cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#ccc'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#eee'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* 아바타 */}
              <div style={{
                width: 50, height: 50, borderRadius: '50%', background: ratingBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 800, color: ratingColor, flexShrink: 0,
              }}>{ins.name[0]}</div>

              {/* 정보 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>{ins.name}</span>
                  <LvBadge level={ins.level} />
                  {ins.status === 'pending' ? (
                    <span style={{ fontSize: 10, color: '#B45309', fontWeight: 700, background: '#FDF3E2', padding: '2px 7px', borderRadius: 5 }}>승인대기</span>
                  ) : ins.status !== 'active' ? (
                    <span style={{ fontSize: 10, color: '#E24B4A', fontWeight: 600 }}>비활성</span>
                  ) : null}
                  {ins.status === 'pending' && (
                    <button onClick={function(e) { e.stopPropagation(); approveInspector(ins); }} style={{
                      padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: '#1D9E75', color: '#fff', fontSize: 11, fontWeight: 700,
                    }} title="승인">승인</button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#888' }}>{ins.area} · {ins.phone}</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span>검차 {ins.total_jobs}건</span>
                  <span>최근 {ins.recent_jobs || 0}건</span>
                  <span>리뷰 {(ins.reviews || []).length}건</span>
                </div>
                {ins.current_lat && ins.current_lng && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px solid #f0f0ec' }}>
                    <span style={{ fontSize: 10, color: '#aaa' }}>위치: {getTimeAgo(ins.location_updated_at)}</span>
                    <a href={'https://map.naver.com/v5/?c=' + ins.current_lng + ',' + ins.current_lat + ',15,0,0,0,dh'}
                      target="_blank" rel="noopener noreferrer"
                      onClick={function(e) { e.stopPropagation(); }}
                      style={{ padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: '#E6F1FB', color: '#185FA5', textDecoration: 'none' }}>지도에서 보기</a>
                  </div>
                )}
              </div>

              {/* 평점 + 삭제 */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#EF9F27' }}>{ins.avg_rating}</span>
                  <span style={{ fontSize: 16, color: '#EF9F27' }}>★</span>
                  <button onClick={function(e) { e.stopPropagation(); setModal({ type: 'danger', title: '평가사 삭제', message: ins.name + ' 평가사를 삭제하시겠습니까?\n진행중인 오더가 있으면 삭제할 수 없습니다.', confirmText: '삭제', onConfirm: function() { closeModal(); authFetch(BASE + '/api/eval/inspectors/' + ins.id, { method: 'DELETE' }).then(function(r) { return r.json(); }).then(function(d) { if (d.success) fetchInspectors(); else setModal({ type: 'danger', title: '실패', message: d.error || '삭제 실패', hideCancel: true, onConfirm: closeModal }); }).catch(function() {}); } }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#ccc', padding: '0 2px', marginLeft: 4 }} title="삭제">×</button>
                </div>
                {/* 미니 바 차트 */}
                <div style={{ display: 'flex', gap: 2, marginTop: 4, justifyContent: 'flex-end' }}>
                  {[
                    { label: '속', val: (ins.avg_scores || {}).speed || 0 },
                    { label: '정', val: (ins.avg_scores || {}).accuracy || 0 },
                    { label: '꼼', val: (ins.avg_scores || {}).detail || 0 },
                  ].map(item => (
                    <div key={item.label} style={{ width: 24, textAlign: 'center' }}>
                      <div style={{
                        height: 3, borderRadius: 2, background: '#f0f0ec', marginBottom: 1,
                      }}>
                        <div style={{
                          height: 3, borderRadius: 2,
                          background: item.val >= 4.5 ? '#1D9E75' : item.val >= 3.5 ? '#EF9F27' : '#E24B4A',
                          width: `${(item.val / 5) * 100}%`,
                        }} />
                      </div>
                      <span style={{ fontSize: 8, color: '#aaa' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}
